import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import {SaveOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import Message from '../../../shared/message/Message.js';
import {CampoSelect} from '../../components/processo/CamposProcesso.jsx';
import {MENSAGEM_ENEM_DUPLICADO, enemDuplicado} from '../../components/processo/processoSelecao.js';

const FormSchema = Yup.object().shape({
    idEnem: Yup.string().required('É obrigatório')
});

const VALORES_VAZIOS = {idEnem: ''};

const useStyles = makeStyles(theme => ({
    paper: {
        width: 500,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    mensagem: {
        marginBottom: theme.spacing(2)
    }
}));

/*
 * ENEM permitido no processo de seleção (_formSelecionarEnem do PrimeFaces).
 * O ano não pode se repetir na lista; na edição, só o item editado é trocado.
 */
export default function DialogEnemProcesso({open, onClose, enem, indice = -1, enemsProcesso = [], enems = [], onSalvar}) {
    const classes = useStyles();
    const [mensagem, setMensagem] = useState('');
    const {control, handleSubmit, reset, formState: {errors, isSubmitting}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    // O ENEM em edição continua na lista mesmo que o apoio não o traga mais.
    const opcoes = enem?.idEnem && !enems.some(item => String(item.id) === String(enem.idEnem))
        ? [{id: enem.idEnem, ano: enem.ano}, ...enems]
        : enems;

    const preencher = () => {
        reset(enem ? {idEnem: enem.idEnem === null || enem.idEnem === undefined ? '' : String(enem.idEnem)} : VALORES_VAZIOS);
        setMensagem('');
    };

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, enem]);

    const submit = valores => {
        const escolhido = opcoes.find(item => String(item.id) === String(valores.idEnem));
        const novo = {...(enem || {}), id: enem?.id ?? null, idEnem: escolhido.id, ano: escolhido.ano};
        if (enemDuplicado(enemsProcesso, novo, indice)) {
            setMensagem(MENSAGEM_ENEM_DUPLICADO);
            return;
        }
        onSalvar(novo);
        onClose();
    };

    /* O diálogo fica dentro do form da página na árvore do React: o submit não pode subir até ele. */
    const aoEnviar = evento => {
        evento.stopPropagation();
        handleSubmit(submit)(evento);
    };

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering: preencher}} scroll='body'
                aria-labelledby='dialog-enem-processo-titulo' classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-enem-processo-titulo'>{enem ? 'Editar Enem' : 'Selecionar Enem'}</DialogTitle>
            <form noValidate autoComplete='off' onSubmit={aoEnviar}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    {!!mensagem && (
                        <div className={classes.mensagem}>
                            <Message type='error' variant='standard'>{mensagem}</Message>
                        </div>
                    )}
                    <Grid container spacing={3}>
                        <CampoSelect control={control} errors={errors} nome='idEnem' rotulo='Enem *' sm={12}
                                     opcoes={opcoes.map(item => ({valor: String(item.id), descricao: String(item.ano ?? item.id)}))}/>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={onClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>Salvar</ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogEnemProcesso.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* ENEM em edição ({id, idEnem, ano}); nulo ao adicionar. */
    enem: PropTypes.object,
    /* Posição do ENEM em edição na lista (-1 ao adicionar). */
    indice: PropTypes.number,
    /* ENEMs já permitidos no processo. */
    enemsProcesso: PropTypes.array,
    /* Opções do apoio: [{id, ano}]. */
    enems: PropTypes.array,
    onSalvar: PropTypes.func.isRequired
};
